export const NEED_IDS = [
  'messaging',
  'message-search',
  'expenses',
  'receipt-ocr',
  'calendar',
  'tone-check',
  'exports',
  'gps-checkins',
  'document-vault',
  'recorded-calls',
] as const;

export type NeedId = (typeof NEED_IDS)[number];

export type NeedOption = {
  id: NeedId;
  code: string;
  label: string;
  description: string;
};

export const NEED_OPTIONS: NeedOption[] = [
  {
    id: 'messaging',
    code: 'm',
    label: 'Documented messaging',
    description: 'Timestamped chat you can export later',
  },
  {
    id: 'message-search',
    code: 's',
    label: 'Search and save messages',
    description: 'Find old messages and bookmark the ones that matter',
  },
  {
    id: 'expenses',
    code: 'e',
    label: 'Shared expenses',
    description: 'Log costs, split them, and track who owes whom',
  },
  {
    id: 'receipt-ocr',
    code: 'r',
    label: 'Receipt scanning',
    description: 'Attach receipts and scan amounts automatically',
  },
  {
    id: 'calendar',
    code: 'c',
    label: 'Custody calendar',
    description: 'Parenting schedules, exceptions, and handoffs',
  },
  {
    id: 'tone-check',
    code: 't',
    label: 'Tone check',
    description: 'Score a draft and get a calmer rewrite before sending',
  },
  {
    id: 'exports',
    code: 'x',
    label: 'Court-ready exports',
    description: 'One-tap message, expense, and calendar reports',
  },
  {
    id: 'gps-checkins',
    code: 'g',
    label: 'GPS exchange check-ins',
    description: 'Location-verified handoffs with on-time tracking',
  },
  {
    id: 'document-vault',
    code: 'v',
    label: 'Document vault',
    description: 'Order copies, school forms, and records in one place',
  },
  {
    id: 'recorded-calls',
    code: 'k',
    label: 'Recorded calls',
    description: 'Record and transcribe calls inside the app',
  },
];

export const DEFAULT_NEED_IDS: NeedId[] = [
  'messaging',
  'expenses',
  'calendar',
  'exports',
];

const NEED_BY_ID = new Map<NeedId, NeedOption>(
  NEED_OPTIONS.map((option) => [option.id, option])
);

const NEED_BY_CODE = new Map<string, NeedId>(
  NEED_OPTIONS.map((option) => [option.code, option.id])
);

export function isNeedId(value: string): value is NeedId {
  return NEED_BY_ID.has(value as NeedId);
}

export function getNeedById(id: NeedId): NeedOption {
  return NEED_BY_ID.get(id) ?? NEED_OPTIONS[0];
}

export function encodeNeedIds(ids: NeedId[]): string {
  const encoded = NEED_OPTIONS.filter((option) => ids.includes(option.id))
    .map((option) => option.code)
    .join(',');
  return encoded || 'none';
}

export function decodeNeedCodes(value: string | undefined | null): NeedId[] {
  if (!value) return [...DEFAULT_NEED_IDS];
  if (value === 'none') return [];
  const decoded = value
    .split(',')
    .map((code) => NEED_BY_CODE.get(code.trim()))
    .filter((id): id is NeedId => Boolean(id));
  return Array.from(new Set(decoded));
}

export function sortNeedIds(ids: NeedId[]): NeedId[] {
  return NEED_OPTIONS.filter((option) => ids.includes(option.id)).map((option) => option.id);
}
